import { useQuery } from '@tanstack/react-query';
import { useState, useRef, useMemo, useEffect } from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { API_URL, get } from 'lib/network/api';
import useClickOutside from 'lib/hooks/useClickOutside';

const fetchParticipant = async () => {
  const response = await get(`${API_URL}/api/user/participant`);
  if (!response || response.error) {
    const err = new Error(response?.message || 'Failed to fetch users');
    if (response?.status) err.response = { status: response.status };
    throw err;
  }
  return response;
};

const isAuthError = (error) => {
  const msg = error?.message?.toLowerCase?.() || '';
  const code = error?.response?.status;
  return code === 401 || msg.includes('token expired') || msg.includes('unauthorized');
};

export default function SelectedParticipant({ value = [], onChange, onAuthRequired, maxQuota }) {
  const dropdownRef = useRef();
  const [openListParticipant, setOpenListParticipant] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(value);
  const [errorMsg, setErrorMsg] = useState('');
  useClickOutside(dropdownRef, () => setOpenListParticipant(false));

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['participant'],
    queryFn: fetchParticipant,
    staleTime: 1000 * 60 * 5,
    retry: (count, err) => !isAuthError(err) && count < 2,
    onError: (err) => {
      if (isAuthError(err)) {
        onAuthRequired?.();
      }
    },
  });

  const isSelected = (userId) => selectedUsers.some((u) => u.id === userId);

  const handleSelect = (user) => {
    let updated = [];
    if (isSelected(user.id)) {
      updated = selectedUsers.filter((u) => u.id !== user.id);
      setErrorMsg('');
    } else {
      if (maxQuota && selectedUsers.length >= maxQuota) {
        setErrorMsg(`You can only select up to ${maxQuota} participant${maxQuota > 1 ? 's' : ''}.`);
        toast.error(
          `Maximum ${maxQuota} participant${maxQuota > 1 ? 's' : ''} allowed for this class pass.`,
        );
        return;
      }

      updated = [...selectedUsers, user];
      setErrorMsg('');
    }

    setSelectedUsers(updated);
    onChange?.(updated);
  };

  const selectedNames = useMemo(() => {
    if (selectedUsers.length === 0) return '';
    return selectedUsers.map((user) => user.name).join(', ');
  }, [selectedUsers]);

  if (isLoading) return <p>Loading users...</p>;
  if (isError) {
    if (isAuthError(error)) return null;
    return <p className="text-red-500">Error: {error.message}</p>;
  }

  return (
    <div className="flex flex-col" ref={dropdownRef}>
      <span className="block text-sm font-medium leading-6 text-kin-text">SELECT PARTICIPANT</span>

      <button
        type="button"
        onClick={() => setOpenListParticipant(!openListParticipant)}
        className="bg-white ring-1 ring-gray-300 rounded-xl py-1.5 pl-4 pr-10 relative text-left mt-1">
        <span className="truncate block">{selectedNames || 'Select Children First...'}</span>
        <IconChevronDown className="absolute right-2 top-2 w-6 h-6 text-kin-text" />
      </button>

      {openListParticipant && (
        <div className="border rounded-xl px-5 py-2 shadow-lg bg-white max-h-64 overflow-auto mt-1">
          {data?.length > 0 ? (
            data.map((user) => {
              const disabled = !isSelected(user.id) && maxQuota && selectedUsers.length >= maxQuota;

              return (
                <label
                  key={user.id}
                  className={`flex items-center gap-2 cursor-pointer py-1 ${
                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}>
                  <input
                    type="checkbox"
                    checked={isSelected(user.id)}
                    onChange={() => !disabled && handleSelect(user)}
                    disabled={disabled}
                    className="w-4 h-4 border-2 border-gray-300 rounded-sm cursor-pointer accent-primary-main
                               checked:bg-primary-main checked:border-primary-main hover:border-primary-main
                               hover:bg-primary-main text-primary-main focus:ring-primary-main"
                  />
                  <span className="text-sm">{user.name}</span>
                </label>
              );
            })
          ) : (
            <p className="text-sm">No users found</p>
          )}
        </div>
      )}

      {errorMsg && <p className="mt-1 text-sm text-red-500">{errorMsg}</p>}
    </div>
  );
}
